import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Wrench, Plus, Trash2, Loader2, X } from "lucide-react";
import { errMsg } from "../../lib/apiErrors";
import { fmtRoDate } from "../../lib/dateFmt";
import { useAuth } from "../../context/AuthContext";

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function RepairsDialog({ item, onClose, onChanged }) {
  const { canDo } = useAuth();
  const canEdit = canDo("INVENTAR", "edit");
  const [repairs, setRepairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    date: todayIso(),
    description: "",
    performed_by: "",
    cost_chf: "",
    parts_used: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/inventory/${item.id}/repairs`);
      setRepairs(r.data || []);
    } catch (e) {
      toast.error(errMsg(e, "Eroare la încărcare reparații"));
      setRepairs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);

  const submit = async () => {
    if (!form.description.trim()) {
      toast.error("Descrierea reparației este obligatorie");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/inventory/${item.id}/repairs`, {
        date: form.date || todayIso(),
        description: form.description.trim(),
        performed_by: form.performed_by.trim() || null,
        cost_chf: form.cost_chf ? parseFloat(form.cost_chf) : null,
        parts_used: form.parts_used.trim() || null,
      });
      toast.success("Reparație înregistrată");
      setShowAdd(false);
      setForm({ date: todayIso(), description: "", performed_by: "", cost_chf: "", parts_used: "" });
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(errMsg(e, "Eroare la salvare"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (rid, desc) => {
    if (!window.confirm(`Ștergi reparația „${desc.slice(0, 80)}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/inventory/${item.id}/repairs/${rid}`);
      toast.success("Reparație ștearsă");
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(errMsg(e, "Eroare la ștergere"));
    } finally {
      setBusy(false);
    }
  };

  const totalCost = repairs.reduce((s, r) => s + (Number(r.cost_chf) || 0), 0);

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onClose && onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="repairs-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench size={18} className="text-orange-600"/>
            Reparații — {item.name}
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Istoricul tuturor reparațiilor pe acest echipament (similar cu autoturismele).
            {item.serial && <span className="ml-2 font-mono">SN: {item.serial}</span>}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-2 py-2 border-b">
          <div className="flex items-center gap-3 text-sm">
            <span className="font-semibold text-zinc-700">{repairs.length} {repairs.length === 1 ? "reparație" : "reparații"}</span>
            {totalCost > 0 && (
              <span className="font-semibold text-orange-700" data-testid="repairs-total-cost">
                Total cost: {totalCost.toFixed(2)} CHF
              </span>
            )}
          </div>
          {canEdit && !showAdd && (
            <Button
              size="sm"
              onClick={() => setShowAdd(true)}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="repairs-add-btn"
            >
              <Plus size={14} className="mr-1"/> Înregistrează reparație
            </Button>
          )}
        </div>

        {/* Add form (collapsible) */}
        {showAdd && canEdit && (
          <div className="p-3 border-b bg-orange-50/40 rounded-md mt-2 space-y-2" data-testid="repairs-add-form">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-bold text-orange-900">Reparație nouă</Label>
              <button type="button" onClick={() => setShowAdd(false)} className="text-zinc-500 hover:text-zinc-800">
                <X size={14}/>
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <Label className="text-[10px] uppercase text-zinc-500">Data *</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="h-9 text-sm"
                  data-testid="repairs-form-date"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[10px] uppercase text-zinc-500">Service / Persoană *</Label>
                <Input
                  value={form.performed_by}
                  onChange={(e) => setForm({ ...form, performed_by: e.target.value })}
                  className="h-9 text-sm"
                  placeholder="ex: Hilti Service / Mecanic intern"
                  data-testid="repairs-form-performed-by"
                />
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-zinc-500">Descriere reparație *</Label>
              <Textarea
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="ex: Înlocuit motor, schimb perii colector"
                className="text-sm"
                data-testid="repairs-form-description"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] uppercase text-zinc-500">Piese folosite</Label>
                <Input
                  value={form.parts_used}
                  onChange={(e) => setForm({ ...form, parts_used: e.target.value })}
                  className="h-9 text-sm"
                  placeholder="ex: Cablu, motor, garnituri"
                  data-testid="repairs-form-parts"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase text-zinc-500">Cost (CHF)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.cost_chf}
                  onChange={(e) => setForm({ ...form, cost_chf: e.target.value })}
                  className="h-9 text-sm"
                  placeholder="ex: 150"
                  data-testid="repairs-form-cost"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={() => setShowAdd(false)} disabled={busy}>Anulează</Button>
              <Button
                size="sm"
                onClick={submit}
                disabled={busy || !form.description.trim()}
                className="bg-orange-600 hover:bg-orange-700 text-white"
                data-testid="repairs-form-submit"
              >
                {busy ? <><Loader2 className="animate-spin mr-1" size={14}/>Se salvează...</> : "Salvează"}
              </Button>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto pr-1 mt-2 space-y-2">
          {loading ? (
            <div className="py-8 text-center text-zinc-500">
              <Loader2 className="inline animate-spin mr-2" size={16}/> Se încarcă...
            </div>
          ) : repairs.length === 0 ? (
            <div className="py-12 text-center text-zinc-500 italic" data-testid="repairs-empty">
              Nicio reparație înregistrată încă.
              {canEdit && <div className="text-[11px] mt-1">Apasă „Înregistrează reparație" pentru a începe.</div>}
            </div>
          ) : (
            <div className="space-y-2" data-testid="repairs-list">
              {repairs.map((r) => (
                <div
                  key={r.id}
                  className="border-l-4 border-orange-400 bg-white rounded shadow-sm p-3"
                  data-testid={`repair-row-${r.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-orange-800 text-sm">📅 {fmtRoDate(r.date) || r.date}</span>
                        {r.performed_by && (
                          <span className="text-[11px] font-semibold bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded">
                            🔧 {r.performed_by}
                          </span>
                        )}
                        {r.cost_chf != null && Number(r.cost_chf) > 0 && (
                          <span className="text-[11px] font-bold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded tabular-nums">
                            {Number(r.cost_chf).toFixed(2)} CHF
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-zinc-800 mt-1.5 whitespace-pre-wrap">{r.description}</div>
                      {r.parts_used && (
                        <div className="text-[11px] text-zinc-600 mt-1">
                          <strong>Piese:</strong> {r.parts_used}
                        </div>
                      )}
                      <div className="text-[10px] text-zinc-400 mt-1.5 italic">
                        Înregistrat de <strong>{r.created_by || "—"}</strong>
                        {r.created_at && <> la {fmtRoDate(r.created_at.slice(0, 10))}</>}
                      </div>
                    </div>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => remove(r.id, r.description)}
                        disabled={busy}
                        className="text-zinc-400 hover:text-red-600 transition-colors p-1 rounded hover:bg-red-50"
                        title="Șterge reparație"
                        data-testid={`repair-delete-${r.id}`}
                      >
                        <Trash2 size={14}/>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 pt-2 border-t mt-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Închide</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
